import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AttorneySelect from '../components/AttorneySelect';
import StopLocationSelect from '../components/StopLocationSelect';
import TypeOfRecordSelect from '../components/TypeOfRecordSelect';
import QuestionSelect from '../components/QuestionSelect';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUSES = ['Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];

const Field = ({ label, field, form, set, type = 'text', editing, readOnly, ...props }) => {
  // Always editable in WorkOrderForm — editing prop accepted but not used for lock
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <input
        type={type}
        value={form[field] || ''}
        onChange={(e) => set(field, e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        {...props}
      />
    </div>
  );
};

const SelectField = ({ label, field, options, form, set, editing }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
    <select
      value={form[field] || ''}
      onChange={(e) => set(field, e.target.value)}
      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
    >
      <option value="">-- Select --</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const EMPTY_FORM = {
  // Stop Location
  stop_location_name: '',
  stop_location_add1_name: '',
  stop_location_attention: '',
  stop_location_address: '',
  stop_location_addr2: '',
  stop_location_city_state_zip: '',
  stop_location_phone: '',
  stop_location_fax: '',
  stop_location_email: '',
  stop_location_website: '',
  stop_location_contact: '',
  stop_location_agent_of_service: '',
  stop_location_service_type: '',
  stop_location_requirements: '',
  stop_location_notes: '',
  stop_location_include_prior_dates: false,
  stop_location_include_to_present: false,
  stop_location_type_records: '',
  // Record Details
  type_of_record: '',
  record_date_from: '',
  record_date_to: '',
  legal_tech: '',
  status: 'Pending',
  rush_order: false,
  records_requested: '',
  ordering_attorney: '',
  question_text: '',
  // Status Tracking
  status_date: '',
  notes_entry: '',
  next_call_date: '',
  served_date: '',
  set_date: '',
  waiver_date: '',
  closed_date: '',
  cancel_date: '',
  add1_notes: '',
  general_notes: '',
  notes: '',
  // Payment
  check_number: '',
  payment_amount: 0,
  payment_date: '',
  prepay: false,
  paid_to: '',
  payments: [],
  // Work Order Pg 2
  firm_2nd_attorney: '',
  name_2nd_attorney: '',
  records_requested_type: false,
  radiology_requested: false,
  rule_11_requested: false,
  review_type: '',
  tracking_number: '',
  courier: '',
  courier_note: '',
  notary: false,
  notary_phone: '',
  certified: false,
  certified_text: '',
  hand_served: false,
  cross_questions: false,
  amended: false,
  redirects: false,
  copies: false,
  pages_count: 0,
  films_count: 0,
  color_pages_count: 0,
  cds_count: 0,
  other_description: '',
  cd_qty: 0,
  cd_type: '',
  cd_items: [],
  others_table: [],
  front_check_by: '',
  back_check_by: '',
  double_check_by: '',
  box_number: '',
  shredded: false,
};

export default function WorkOrderForm() {
  const { id: causeId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [causeNumber, setCauseNumber] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  // Invoice local state (new WO has no invoices yet)
  const [invoices, setInvoices] = useState([]);
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' });

  const TAB_LABELS = ['Stop Location', 'Work Order Update', 'Work Order Pg 2', 'Invoice'];

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  useEffect(() => {
    if (!causeId || !token) return;
    axios.get(`${API_URL}/api/causes/${causeId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCauseNumber(res.data.cause_number || '');
        const clientAtty = (res.data.attorneys || []).find(a => a.type === 'Client');
        if (clientAtty) {
          setForm(f => ({ ...f, ordering_attorney: clientAtty.attorney_name || '' }));
        }
      })
      .catch(() => {});
  }, [causeId, token]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // Convert numeric fields before sending (empty string -> 0)
      const payload = {
        ...form,
        payment_amount: parseFloat(form.payment_amount) || 0,
        pages_count: parseInt(form.pages_count) || 0,
        films_count: parseInt(form.films_count) || 0,
        color_pages_count: parseInt(form.color_pages_count) || 0,
        cds_count: parseInt(form.cds_count) || 0,
        cd_qty: parseInt(form.cd_qty) || 0,
      };
      const res = await axios.post(`${API_URL}/api/causes/${causeId}/work-orders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Post any invoices added in the Invoice tab
      if (invoices.length > 0 && res.data?.id) {
        for (const inv of invoices) {
          await axios.post(`${API_URL}/api/work-orders/${res.data.id}/invoices`, {
            ...inv,
            amount: parseFloat(inv.amount) || 0,
          }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      navigate(`/causes/${causeId}`, { state: { tab: 2 } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };

  const addInvoiceToList = () => {
    setInvoices(prev => [...prev, { ...invForm }]);
    setShowInvForm(false);
    setInvForm({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">New Work Order — {causeNumber || 'Case'}</h1>
        <button
          onClick={() => navigate(`/causes/${causeId}`)}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap border-b mb-4 gap-0">
        {TAB_LABELS.map((t, i) => (
          <button
            key={t}
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab 0: Stop Location ── */}
      {activeTab === 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left section */}
            <div className="space-y-3">
              <StopLocationSelect
                label="Stop Location Name"
                value={form.stop_location_name || ''}
                onChange={(id, name, addr1, city, state, zip, phone, fax, email, contactName, typeOfRecord, notes) => {
                  setForm(f => ({
                    ...f,
                    stop_location_name: name,
                    stop_location_address: addr1,
                    stop_location_city_state_zip: [city, state, zip].filter(Boolean).join(', '),
                    stop_location_phone: phone,
                    stop_location_fax: fax,
                    stop_location_email: email,
                    stop_location_contact: contactName,
                    stop_location_type_records: typeOfRecord || f.stop_location_type_records,
                    stop_location_notes: notes,
                  }));
                }}
              />
              <Field label="Add1 Name" field="stop_location_add1_name" form={form} set={set} editing={true} />
              <Field label="Attention" field="stop_location_attention" form={form} set={set} editing={true} />
              <TypeOfRecordSelect
                label="Type / Records"
                value={form.stop_location_type_records || ''}
                onChange={v => setForm(f => ({ ...f, stop_location_type_records: v }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Record Date From" field="record_date_from" type="date" form={form} set={set} editing={true} />
                <Field label="Record Date To" field="record_date_to" type="date" form={form} set={set} editing={true} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.stop_location_include_prior_dates || false} onChange={(e) => set('stop_location_include_prior_dates', e.target.checked)} />
                  Include Prior Dates
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.stop_location_include_to_present || false} onChange={(e) => set('stop_location_include_to_present', e.target.checked)} />
                  Include to Present
                </label>
              </div>
              <Field label="Address" field="stop_location_address" form={form} set={set} editing={true} />
              <Field label="Adrs line 2" field="stop_location_addr2" form={form} set={set} editing={true} />
              <Field label="City/State/Zip" field="stop_location_city_state_zip" form={form} set={set} editing={true} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" field="stop_location_phone" form={form} set={set} editing={true} />
                <Field label="FAX" field="stop_location_fax" form={form} set={set} editing={true} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" field="stop_location_email" form={form} set={set} editing={true} />
                <Field label="Website" field="stop_location_website" form={form} set={set} editing={true} />
              </div>
              <Field label="Contact" field="stop_location_contact" form={form} set={set} editing={true} />
              <Field label="Agent of Service" field="stop_location_agent_of_service" form={form} set={set} editing={true} />
              <Field label="Service Type" field="stop_location_service_type" form={form} set={set} editing={true} />
              <Field label="Requirements" field="stop_location_requirements" form={form} set={set} editing={true} />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Stop Location Notes</label>
                <textarea value={form.stop_location_notes || ''} onChange={(e) => set('stop_location_notes', e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-yellow-50 focus:ring-1 focus:ring-blue-500 focus:outline-none" />
              </div>
            </div>
            {/* Right section */}
            <div className="space-y-3">
              <QuestionSelect
                label="Question Text"
                value={form.question_text || ''}
                onChange={v => setForm(f => ({ ...f, question_text: v }))}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 1: Work Order Update ── */}
      {activeTab === 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Top header fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Field label="Legal Tech" field="legal_tech" form={form} set={set} editing={true} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Status</label>
              <select value={form.status || ''} onChange={(e) => set('status', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Field label="Status Date" field="status_date" type="date" form={form} set={set} editing={true} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Notes Entry</label>
              <div className="flex gap-1">
                <input value={form.notes_entry || ''} onChange={(e) => set('notes_entry', e.target.value)} className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
            </div>
          </div>

          {/* Status tracking dates */}
          <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Status Tracking</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Field label="Next Call" field="next_call_date" type="date" form={form} set={set} editing={true} />
            <Field label="Served" field="served_date" type="date" form={form} set={set} editing={true} />
            <Field label="Set" field="set_date" type="date" form={form} set={set} editing={true} />
            <Field label="Waiver" field="waiver_date" type="date" form={form} set={set} editing={true} />
            <Field label="Closed" field="closed_date" type="date" form={form} set={set} editing={true} />
            <Field label="Cancel" field="cancel_date" type="date" form={form} set={set} editing={true} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Add1 Notes</label>
              <textarea value={form.add1_notes || ''} onChange={(e) => set('add1_notes', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">General Notes</label>
              <textarea value={form.general_notes || ''} onChange={(e) => set('general_notes', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-yellow-50" />
            </div>
          </div>

          {/* Payment section */}
          <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Payment</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Field label="Ck# (99=CC)" field="check_number" form={form} set={set} editing={true} />
            <Field label="Amt" field="payment_amount" type="number" form={form} set={set} editing={true} />
            <Field label="Payment Date" field="payment_date" type="date" form={form} set={set} editing={true} />
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.prepay || false} onChange={(e) => set('prepay', e.target.checked)} />
                PP Prepay
              </label>
            </div>
            <Field label="Paid to" field="paid_to" form={form} set={set} editing={true} />
          </div>
          <button
            type="button"
            onClick={() => {
              const p = { check_number: form.check_number || '', amount: form.payment_amount || 0, date: form.payment_date || '', paid_to: form.paid_to || '' };
              set('payments', [...(form.payments || []), p]);
            }}
            className="px-3 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-700 mb-3"
          >
            + Add Payment
          </button>
          {(form.payments || []).length > 0 && (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-1.5 text-xs text-gray-600">Ck#</th>
                  <th className="text-right p-1.5 text-xs text-gray-600">Amount</th>
                  <th className="text-left p-1.5 text-xs text-gray-600">Date</th>
                  <th className="text-left p-1.5 text-xs text-gray-600">Paid To</th>
                  <th className="p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {(form.payments || []).map((p, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-1.5">{p.check_number}</td>
                    <td className="p-1.5 text-right">${Number(p.amount || 0).toFixed(2)}</td>
                    <td className="p-1.5">{p.date}</td>
                    <td className="p-1.5">{p.paid_to}</td>
                    <td className="p-1.5">
                      <button type="button" onClick={() => set('payments', (form.payments || []).filter((_, idx) => idx !== i))} className="text-red-600 text-xs hover:underline">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Read-only stop location display */}
          <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Stop Location (Read-Only)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded bg-blue-50">
            <div><label className="block text-xs text-gray-500">Name</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_name || '-'}</div></div>
            <div><label className="block text-xs text-gray-500">Address</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_address || '-'}</div></div>
            <div><label className="block text-xs text-gray-500">City/State/Zip</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_city_state_zip || '-'}</div></div>
            <div><label className="block text-xs text-gray-500">Phone</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_phone || '-'}</div></div>
            <div><label className="block text-xs text-gray-500">FAX</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_fax || '-'}</div></div>
            <div><label className="block text-xs text-gray-500">Contact</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{form.stop_location_contact || '-'}</div></div>
          </div>
        </div>
      )}

      {/* ── Tab 2: Work Order Pg 2 ── */}
      {activeTab === 2 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column */}
            <div className="space-y-4">
              {/* Ordered Records */}
              <h4 className="font-semibold text-gray-600 border-b pb-1">Ordered Records</h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Firm 2nd Atty" field="firm_2nd_attorney" form={form} set={set} editing={true} />
                <Field label="Name" field="name_2nd_attorney" form={form} set={set} editing={true} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.records_requested_type || false} onChange={(e) => set('records_requested_type', e.target.checked)} /> Records</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.radiology_requested || false} onChange={(e) => set('radiology_requested', e.target.checked)} /> Radiology</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.rule_11_requested || false} onChange={(e) => set('rule_11_requested', e.target.checked)} /> Rule 11</label>
              </div>
              <SelectField label="Review" field="review_type" options={['Review', 'Partial Review', 'Full Review', 'No Review']} form={form} set={set} editing={true} />

              {/* Others table */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-600">Others</label>
                  <button type="button" onClick={() => set('others_table', [...(form.others_table || []), { firm: '', name: '', records: false, radiology: false, rule_11: false, review: '' }])} className="text-xs text-blue-600 hover:underline">+ Add</button>
                </div>
                {(form.others_table || []).length > 0 && (
                  <table className="w-full text-xs border">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="p-1 text-left">Firm</th>
                        <th className="p-1 text-left">Name</th>
                        <th className="p-1">Rec</th>
                        <th className="p-1">Rad</th>
                        <th className="p-1">R11</th>
                        <th className="p-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.others_table || []).map((o, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1"><input value={o.firm || ''} onChange={(e) => { const arr = [...(form.others_table || [])]; arr[i] = { ...arr[i], firm: e.target.value }; set('others_table', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                          <td className="p-1"><input value={o.name || ''} onChange={(e) => { const arr = [...(form.others_table || [])]; arr[i] = { ...arr[i], name: e.target.value }; set('others_table', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                          <td className="p-1 text-center"><input type="checkbox" checked={o.records || false} onChange={(e) => { const arr = [...(form.others_table || [])]; arr[i] = { ...arr[i], records: e.target.checked }; set('others_table', arr); }} /></td>
                          <td className="p-1 text-center"><input type="checkbox" checked={o.radiology || false} onChange={(e) => { const arr = [...(form.others_table || [])]; arr[i] = { ...arr[i], radiology: e.target.checked }; set('others_table', arr); }} /></td>
                          <td className="p-1 text-center"><input type="checkbox" checked={o.rule_11 || false} onChange={(e) => { const arr = [...(form.others_table || [])]; arr[i] = { ...arr[i], rule_11: e.target.checked }; set('others_table', arr); }} /></td>
                          <td className="p-1"><button type="button" onClick={() => set('others_table', (form.others_table || []).filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">x</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Tracking section */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Tracking</h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Number" field="tracking_number" form={form} set={set} editing={true} />
                <Field label="Courier" field="courier" form={form} set={set} editing={true} />
              </div>
              <Field label="Courier Note" field="courier_note" form={form} set={set} editing={true} />
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.notary || false} onChange={(e) => set('notary', e.target.checked)} /> Notary</label>
                {form.notary && <Field label="Phone" field="notary_phone" form={form} set={set} editing={true} />}
              </div>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.certified || false} onChange={(e) => set('certified', e.target.checked)} /> Certified</label>
                {form.certified && <Field label="Details" field="certified_text" form={form} set={set} editing={true} />}
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.hand_served || false} onChange={(e) => set('hand_served', e.target.checked)} /> Hand Served</label>
              <label className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded ${form.rush_order ? 'bg-red-100 text-red-800' : 'text-gray-700'}`}>
                <input type="checkbox" checked={form.rush_order || false} onChange={(e) => set('rush_order', e.target.checked)} /> Rush Order
              </label>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* Checkboxes */}
              <h4 className="font-semibold text-gray-600 border-b pb-1">Options</h4>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.cross_questions || false} onChange={(e) => set('cross_questions', e.target.checked)} /> Cross Questions</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.amended || false} onChange={(e) => set('amended', e.target.checked)} /> Amended</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.redirects || false} onChange={(e) => set('redirects', e.target.checked)} /> Redirects</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.copies || false} onChange={(e) => set('copies', e.target.checked)} /> Copies</label>
              </div>

              {/* Completed information */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Completed</h4>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Pages" field="pages_count" type="number" form={form} set={set} editing={true} />
                <Field label="Films" field="films_count" type="number" form={form} set={set} editing={true} />
                <Field label="Color Pages" field="color_pages_count" type="number" form={form} set={set} editing={true} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CDs Old Vers" field="cds_count" type="number" form={form} set={set} editing={true} />
                <Field label="Other" field="other_description" form={form} set={set} editing={true} />
              </div>

              {/* CD Items */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="QTY" field="cd_qty" type="number" form={form} set={set} editing={true} />
                <SelectField label="Description" field="cd_type" options={['CD', 'DVD', 'USB', 'Digital Download']} form={form} set={set} editing={true} />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-600">CD Items</label>
                  <button type="button" onClick={() => set('cd_items', [...(form.cd_items || []), { qty: 0, description: '' }])} className="text-xs text-blue-600 hover:underline">+ Add</button>
                </div>
                {(form.cd_items || []).length > 0 && (
                  <table className="w-full text-xs border">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="p-1 text-left">QTY</th>
                        <th className="p-1 text-left">Description</th>
                        <th className="p-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.cd_items || []).map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1"><input type="number" value={item.qty || 0} onChange={(e) => { const arr = [...(form.cd_items || [])]; arr[i] = { ...arr[i], qty: parseInt(e.target.value) || 0 }; set('cd_items', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                          <td className="p-1"><input value={item.description || ''} onChange={(e) => { const arr = [...(form.cd_items || [])]; arr[i] = { ...arr[i], description: e.target.value }; set('cd_items', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                          <td className="p-1"><button type="button" onClick={() => set('cd_items', (form.cd_items || []).filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">x</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Production verification */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Production Verification</h4>
              <Field label="Front Check Performed By" field="front_check_by" form={form} set={set} editing={true} />
              <Field label="Back Check Performed By" field="back_check_by" form={form} set={set} editing={true} />
              <Field label="Double Check Performed By" field="double_check_by" form={form} set={set} editing={true} />
              <Field label="Box Number" field="box_number" form={form} set={set} editing={true} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.shredded || false} onChange={(e) => set('shredded', e.target.checked)} /> Shredded</label>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 3: Invoice ── */}
      {activeTab === 3 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Invoices (saved with work order)</h3>
            <button
              type="button"
              onClick={() => { setShowInvForm(true); setInvForm({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' }); }}
              className="px-3 py-1.5 text-sm text-white rounded"
              style={{ backgroundColor: '#1a2744' }}
            >
              + Add Invoice
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2 font-medium text-gray-600">#</th>
                  <th className="text-left p-2 font-medium text-gray-600">Description</th>
                  <th className="text-right p-2 font-medium text-gray-600">Amount</th>
                  <th className="text-left p-2 font-medium text-gray-600">Status</th>
                  <th className="text-left p-2 font-medium text-gray-600">Invoice Date</th>
                  <th className="text-left p-2 font-medium text-gray-600">Due Date</th>
                  <th className="text-left p-2 font-medium text-gray-600">Notes</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{i + 1}</td>
                    <td className="p-2">{inv.description}</td>
                    <td className="p-2 text-right">${(parseFloat(inv.amount) || 0).toFixed(2)}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.status === 'Paid' ? 'bg-green-100 text-green-800' :
                        inv.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{inv.status}</span>
                    </td>
                    <td className="p-2 text-gray-500">{inv.invoice_date || '-'}</td>
                    <td className="p-2 text-gray-500">{inv.due_date || '-'}</td>
                    <td className="p-2 text-gray-500 max-w-[150px] truncate">{inv.notes || '-'}</td>
                    <td className="p-2">
                      <button type="button" onClick={() => setInvoices(prev => prev.filter((_, idx) => idx !== i))} className="text-red-600 text-xs hover:underline">Remove</button>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-center text-gray-400">No invoices added yet</td></tr>
                )}
              </tbody>
              {invoices.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="p-2" colSpan={2}>Total</td>
                    <td className="p-2 text-right">${invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(2)}</td>
                    <td colSpan={5}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {showInvForm && (
            <div className="mt-4 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-700 mb-3">New Invoice</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
                  <input type="number" step="0.01" value={invForm.amount} onChange={(e) => setInvForm((f) => ({ ...f, amount: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <input value={invForm.description} onChange={(e) => setInvForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select value={invForm.status} onChange={(e) => setInvForm((f) => ({ ...f, status: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded bg-white">
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date</label>
                  <input type="date" value={invForm.invoice_date || ''} onChange={(e) => setInvForm((f) => ({ ...f, invoice_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                  <input type="date" value={invForm.due_date || ''} onChange={(e) => setInvForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                  <input value={invForm.notes || ''} onChange={(e) => setInvForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button type="button" onClick={addInvoiceToList} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
                  Add to List
                </button>
                <button type="button" onClick={() => setShowInvForm(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Bottom Navigation & Action Buttons ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 py-4 mt-2">
        {/* Previous / Next */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab(t => Math.max(0, t - 1))}
            disabled={activeTab === 0}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300 disabled:opacity-40"
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(t => Math.min(TAB_LABELS.length - 1, t + 1))}
            disabled={activeTab === TAB_LABELS.length - 1}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
        {/* Save / Cancel */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-white rounded text-sm font-medium"
            style={{ backgroundColor: '#1a2744' }}
          >
            {saving ? 'Saving...' : 'Save Work Order'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/causes/${causeId}`)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
